// Search field for selecting constraint targets (categories or citizens).
// Filters data by text input, shows a suggestion dropdown, and renders
// selected items as removable chips inside the SearchBar.
import { useEffect, useRef, useState } from "react";
import { useAppSelector } from "../../store/hooks";
import SearchBar from "../search/SearchBar";
import searchStyles from "../../styles/search.module.css";
import ColouredRoundWrapper from "../UI/wrappers/ColouredRoundWrapper";
import GreyOpacityText from "../UI/textFields/GreyOpacityText";
import { Client } from "../../types/ganttChart";
import { ConcernCategory } from "../../types/constraint";
import labels from "../../utils/labels";
import useSearchSuggestions from "../../hooks/useSearchSuggestions";
import useClickOutside from "../../hooks/useClickOutside";
import { formatCitizenAddress } from "../../service/citizenAddress";

type SearchableItem = ConcernCategory | Client;

const getItemId = (item: SearchableItem): number =>
  "idServiceClient" in item ? item.idServiceClient : Number(item.id);

const getItemLabel = (item: SearchableItem): string =>
  "idServiceClient" in item ? item.description : item.name;

const getItemAddress = (item: SearchableItem): string | undefined =>
  "idServiceClient" in item ? formatCitizenAddress(item) : undefined;

const AddConstraintSearch = ({
  isCategoryConstraint,
  onSelect,
  excludedTargetIds = [],
}: {
  isCategoryConstraint: boolean;
  onSelect?: (id: number | undefined, description?: string) => void;
  // Targets already held by the employee/citizen the planner is editing — passed in rather than
  // computed here, this component stays purely presentational, told what to hide, not asked to
  // work out the clash itself.
  excludedTargetIds?: number[];
}) => {
  const categories = useAppSelector(
    (state) => state.constraintSlice.categories,
  );
  const clients = useAppSelector((state) => state.constraintSlice.clients);

  const [selectedConstraint, setSelectedConstraint] = useState<
    SearchableItem[]
  >([]);
  const [input, setInput] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const chipRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const previousChipCountRef = useRef(0);

  const { suggestions: clientSuggestions, isOpen: clientIsOpen } =
    useSearchSuggestions(
      input,
      clients,
      selectedConstraint,
      getItemId,
      getItemLabel,
      getItemAddress,
    );

  const categoryItems = categories.filter(
    (item) =>
      !selectedConstraint.some((s) => getItemId(s) === getItemId(item)) &&
      (input === "" ||
        getItemLabel(item).toLowerCase().includes(input.toLowerCase())),
  );

  // Hidden rather than shown-disabled: the planner can already see the existing hensyn in the
  // list above, so a disabled row would only repeat information without adding anything.
  const dropdownItems = (
    isCategoryConstraint ? categoryItems : clientSuggestions
  ).filter((item) => !excludedTargetIds.includes(getItemId(item)));
  const showDropdown = isCategoryConstraint ? isFocused : clientIsOpen;

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || dropdownItems.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) =>
        index < dropdownItems.length - 1 ? index + 1 : 0,
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) =>
        index > 0 ? index - 1 : dropdownItems.length - 1,
      );
    } else if (event.key === "Enter") {
      if (activeIndex >= 0) {
        event.preventDefault();
        handleSelect(dropdownItems[activeIndex]);
      }
    } else if (event.key === "Escape") {
      event.preventDefault();
      setIsFocused(false);
      setActiveIndex(-1);
    }
  };

  const closeDropdown = () => {
    setInput("");
    setIsFocused(false);
    setActiveIndex(-1);
  };

  useClickOutside(searchRef, closeDropdown);

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (
      searchRef.current &&
      !searchRef.current.contains(event.relatedTarget as Node)
    ) {
      closeDropdown();
    }
  };

  const handleSelect = (item: SearchableItem) => {
    setSelectedConstraint((prev) => [...prev, item]);
    onSelect?.(getItemId(item), getItemLabel(item));
    setInput("");
    setIsFocused(false);
    setActiveIndex(-1);
  };

  const handleRemove = (item: SearchableItem) => {
    setSelectedConstraint((prev) =>
      prev.filter((c) => getItemId(c) !== getItemId(item)),
    );
    onSelect?.(undefined);
  };

  // The search input unmounts once a filter is applied (allowFurtherSearch is false), so
  // whatever had focus when a chip is added/removed disappears from under the user — land
  // on the new chip after selecting, and back on the search input (or the next chip) after
  // removing, instead of leaving focus to fall wherever the browser defaults to.
  useEffect(() => {
    const previousCount = previousChipCountRef.current;
    const currentCount = selectedConstraint.length;

    if (currentCount > previousCount) {
      const lastItem = selectedConstraint[currentCount - 1];
      chipRefs.current.get(getItemId(lastItem))?.focus();
    } else if (currentCount < previousCount) {
      if (currentCount === 0) {
        inputRef.current?.focus();
      } else {
        chipRefs.current.get(getItemId(selectedConstraint[0]))?.focus();
      }
    }

    previousChipCountRef.current = currentCount;
  }, [selectedConstraint]);

  const handleClearAll = () => {
    setInput("");
    setSelectedConstraint([]);
    setActiveIndex(-1);
    onSelect?.(undefined);
  };

  const chips = selectedConstraint.map((item) => (
    <ColouredRoundWrapper
      key={getItemId(item)}
      ref={(el) => {
        if (el) chipRefs.current.set(getItemId(item), el);
        else chipRefs.current.delete(getItemId(item));
      }}
      bgColor="#F9F9F9"
      width="fit-content"
      classes="border flex rounded-full whitespace-nowrap px-2.5 justify-between cursor-pointer max-w-[500px]"
      interactive
      ariaLabel={labels.removeSearchFilter(getItemLabel(item))}
      onClick={() => handleRemove(item)}
    >
      <GreyOpacityText size="SMALL">
        {[getItemLabel(item), getItemAddress(item)].filter(Boolean).join(" ")}
      </GreyOpacityText>
    </ColouredRoundWrapper>
  ));

  return (
    <div className="relative" ref={searchRef} onBlur={handleBlur}>
      <SearchBar
        input={input}
        onInputChange={(value) => {
          setInput(value);
          setActiveIndex(-1);
        }}
        appliedFilters={chips}
        onClearAll={handleClearAll}
        placeholder={
          isCategoryConstraint
            ? labels.searchCategoryConstraint
            : labels.searchCitizenConstraint
        }
        allowFurtherSearch={false}
        onFocus={() => setIsFocused(true)}
        inputRef={inputRef}
        onInputKeyDown={handleInputKeyDown}
      />
      {showDropdown && dropdownItems.length > 0 && (
        <ul
          role="listbox"
          aria-label={
            isCategoryConstraint
              ? labels.searchCategoryConstraint
              : labels.searchCitizenConstraint
          }
          aria-activedescendant={
            activeIndex >= 0
              ? `constraint-suggestion-${activeIndex}`
              : undefined
          }
          className={searchStyles.searchSugeestionsWrapper}
          // Options aren't focusable, so a plain click would blur the input first — that blur
          // closes the dropdown (see handleBlur) before the click's onSelect ever runs.
          // Blocking the mousedown's default focus change keeps the input focused through the
          // click, matching how selection already works via keyboard.
          onMouseDown={(event) => event.preventDefault()}
        >
          {dropdownItems.map((item, index) => (
            <li
              key={getItemId(item)}
              id={`constraint-suggestion-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              onClick={() => handleSelect(item)}
              onMouseEnter={() => setActiveIndex(index)}
              className={`px-4 h-[38px] cursor-pointer text-black w-[100%] p-2.5 gap-2.5 rounded-[50px] bg-[#F9F9F9] list-none ${
                index === activeIndex
                  ? "bg-gray-200 outline outline-2 outline-[var(--col-border-focus)]"
                  : "hover:bg-gray-100"
              }`}
            >
              <div className="flex flex-row items-center gap-1">
                {getItemLabel(item)}
                {getItemAddress(item) && (
                  <span style={{ color: "var(--col-text-secondary)" }}>
                    {getItemAddress(item)}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AddConstraintSearch;
